import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import {
  CheckIcon,
  SelectorIcon as ChevronUpDownIcon,
  PlusIcon,
} from '@heroicons/react/solid';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import naturalCompare from 'string-natural-compare';
import { SearchInput } from '../../FindAddContact/SearchInput';

const byNaturalValue = (a: string, b: string) =>
  naturalCompare(a, b, { caseInsensitive: true });

type Props = {
  label?: string;
  items?: string[]; // Make optional
  value?: string; // Make optional
  onChange: (newValue: string) => void;
  disabled?: boolean;
  disabledItems?: string[];
  autoFocus?: boolean;
  /** When true the "Create" option is always displayed, even when nothing is entered in the input */
  createAlwaysShown?: boolean;
  /** Custom text for the create option when createAlwaysShown is true and no query */
  createText?: string;
  /** When false, never show the create option (even if query not in list) */
  showCreateOption?: boolean;
  sortItems?: boolean;
  required?: boolean;
  placeholder?: string;
  /** When true, shows a SearchInput component for filtering, similar to SiteSelect */
  searchable?: boolean;
  error?: string;
  /** Control data-usetiful attribute in the underlying Combobox (defaults to empty string there) */
  dataUsetiful?: string;
};

function UICombobox({
  label,
  items = [], // Default to empty array
  value = '', // Default to empty string
  onChange,
  disabled = false,
  disabledItems = [],
  createAlwaysShown,
  createText,
  showCreateOption = true,
  sortItems = true,
  required = false,
  placeholder,
  searchable = false,
  error,
  dataUsetiful = '',
}: Props) {
  const listboxOptionsRef = useRef<HTMLElement>(null);
  const [query, setQuery] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Apply animation when error appears
  useEffect(() => {
    if (error && !isAnimating) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Combobox error detected, starting animation');
      }
      setIsAnimating(true);

      // Clear animation after 1820ms (same as CreateJobForm)
      timeoutRef.current = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Combobox animation timeout, clearing animation');
        }
        setIsAnimating(false);
      }, 1820);
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [error, isAnimating]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle loading state
  if (!items) {
    return (
      <div
        className={classNames(
          'relative mt-0.5 w-full',
          label && !error && 'mb-5'
        )}
      >
        {label && (
          <Label
            className={classNames(
              disabled ? 'text-grey-30' : 'text-grey-50',
              'mb-1.5 ml-1 text-sm font-medium'
            )}
          >
            {label}
            {required && <span className='pl-0.5 text-brand'>*</span>}
          </Label>
        )}
        <div
          className={classNames(
            'flex w-full cursor-default overflow-hidden rounded border border-grey-20 bg-white px-3 py-2.5 text-left',
            error && 'border-red-500',
            isAnimating && 'animate-shake'
          )}
        >
          <span className='text-gray-500'>Loading...</span>
        </div>
        {error && <p className='ErrorMessage'>{error}</p>}
      </div>
    );
  }

  const sorted = sortItems ? [...items].sort(byNaturalValue) : items;

  const filtered =
    query === ''
      ? sorted
      : sorted.filter((item) =>
          item
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

  return (
    <div
      className={classNames(
        'relative mt-0.5 w-full',
        label && !error && 'mb-5'
      )}
    >
      <Listbox value={value} onChange={onChange} disabled={disabled}>
        {label && (
          <Label
            className={classNames(
              disabled ? 'text-grey-30' : 'text-grey-50',
              'mb-1.5 ml-1 text-sm font-medium'
            )}
          >
            {label}
            {required && <span className='pl-0.5 text-brand'>*</span>}
          </Label>
        )}
        <ListboxButton
          className={classNames(
            'relative w-full cursor-pointer overflow-hidden rounded border border-grey-20 bg-white px-3 py-2.5 pr-10 text-left outline-none focus:border-brand focus:ring-1 focus:ring-brand',
            disabled && 'opacity-20',
            !value && 'text-gray-500',
            error && 'border-red-500',
            isAnimating && 'animate-shake'
          )}
        >
          <span className='block truncate'>
            {value || placeholder || 'Select...'}
          </span>
          <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
            <ChevronUpDownIcon
              className='h-5 w-5 text-gray-400'
              aria-hidden='true'
            />
          </span>
        </ListboxButton>

        <ListboxOptions
          ref={listboxOptionsRef}
          transition
          portal
          data-usetiful={dataUsetiful}
          anchor='bottom start'
          className={classNames(
            'fixed z-[10000] my-1 mt-1 w-[var(--button-width)] overflow-auto rounded-md bg-white pb-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
            '[--anchor-max-height:max(20rem,var(--anchor-available-height,30rem))]',
            '[--anchor-padding:1rem]',
            '[--anchor-gap:4px]',
            'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
            'flex flex-col'
          )}
        >
          {searchable && (
            <div className='sticky -top-1 z-50 -mt-1 mb-1 bg-white p-1 pb-0 pt-3'>
              <SearchInput
                className='rounded-full border-none bg-grey-3'
                inputProps={{ className: 'bg-grey-3' }}
                placeholder={label || 'Search items...'}
                setQuery={setQuery}
                query={query}
              />
            </div>
          )}

          <div className='overflow-y-auto py-1'>
            {showCreateOption &&
              (createAlwaysShown ||
                (query.length > 0 && !items.includes(query))) && (
                <ListboxOption
                  value={query || '$NEW'}
                  className='relative cursor-default select-none py-2 pl-10 pr-4 text-primary active:bg-shade-12 data-[focus]:bg-shade-10 data-[selected]:bg-shade-15'
                >
                  {({ focus }) => (
                    <>
                      <span className='block truncate text-base'>
                        {query ? `Create "${query}"` : createText || 'Create'}
                      </span>
                      <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                          focus && 'text-brand'
                        }`}
                      >
                        <PlusIcon className='h-5 w-5' aria-hidden='true' />
                      </span>
                    </>
                  )}
                </ListboxOption>
              )}
            {filtered.map((item) => (
              <ListboxOption
                key={item}
                value={item}
                disabled={disabledItems.includes(item)}
                className='relative cursor-default select-none py-2 pl-10 pr-4 text-primary active:bg-shade-12 data-[focus]:bg-shade-10 data-[selected]:bg-shade-15 data-[disabled]:opacity-75'
              >
                {({ selected, focus }) => (
                  <>
                    <span
                      className={classNames(
                        'block truncate text-base',
                        selected ? 'font-medium' : 'font-normal'
                      )}
                    >
                      {item}
                    </span>
                    {selected ? (
                      <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                          focus && 'text-brand'
                        }`}
                      >
                        <CheckIcon className='h-5 w-5' aria-hidden='true' />
                      </span>
                    ) : null}
                  </>
                )}
              </ListboxOption>
            ))}
          </div>
        </ListboxOptions>
      </Listbox>
      {error && <p className='ErrorMessage mb-5 mt-1'>{error}</p>}
    </div>
  );
}

export { UICombobox as Combobox };
